// View-model types for the treasury refund-requests module. Produced
// by ../api/refundsParser.ts. Consumers bind to these — never to the
// wire DTOs in ../api/refundsContracts.ts.

export enum REFUND_STATE {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  SETTLED = 'settled',
}

export type RefundState = `${REFUND_STATE}`

export enum REFUND_METHOD {
  FIAT_WALLET = 'fiat_wallet',
  PAYMENT_GATEWAY = 'payment_gateway',
}

export type RefundMethod = `${REFUND_METHOD}`

// ─── Row (list response item) ────────────────────────────────────────────────

export interface RefundRow {
  id: string
  orderId: string
  orderReference: string
  // Non-null when the source order was cancelled (vs an admin rejection).
  orderCancelledAt: string | null
  investorId: string
  investorName: string
  investorEmail: string
  amount: string
  currencyCode: string
  refundState: RefundState
  refundMethod: RefundMethod
  notes: string | null
  requestedAt: string
  approvedAt: string | null
  approvedBy: string | null
  settledAt: string | null
  settlementReference: string | null
}

export interface RefundsListResponse {
  data: RefundRow[]
  meta: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

// ─── Status counts ───────────────────────────────────────────────────────────

export interface RefundStatusCounts {
  pending: number
  settled: number
  rejected: number
}

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface RefundOrder {
  id: string
  orderReference: string
  orderStatus: string
  propertyName: string | null
  tokenQuantity: number | null
  netAmount: string
  currencyCode: string
  paymentMethod: string | null
  createdAt: string
  paymentConfirmedAt: string | null
  rejectedAt: string | null
  cancelledAt: string | null
}

export interface RefundInvestor {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  kycStatus: string | null
}

export interface RefundAudit {
  approvedBy: string | null
  approvedAt: string | null
  settledAt: string | null
  settlementReference: string | null
}

export interface RefundDetail {
  id: string
  orderId: string
  investorId: string
  amount: string
  currencyCode: string
  refundState: RefundState
  refundMethod: RefundMethod
  notes: string | null
  requestedAt: string
  approvedAt: string | null
  approvedBy: string | null
  settledAt: string | null
  settlementReference: string | null
  order: RefundOrder
  investor: RefundInvestor
  audit: RefundAudit
}

// ─── Request params / inputs ─────────────────────────────────────────────────

export interface GetRefundsParams {
  page?: number
  limit?: number
  refundState?: RefundState
  refundMethod?: RefundMethod
  fromDate?: string
  toDate?: string
  investorId?: string
  investorName?: string
  investorEmail?: string
  sortOrder?: 'asc' | 'desc'
}

export interface ApproveRefundInput {
  id: string
  notes?: string
  idempotencyKey: string
}

export interface RejectRefundInput {
  id: string
  reason: string
  idempotencyKey: string
}
